import React, { useState } from 'react';
import {
  Modal,
  Typography,
  Box,
  Button,
  IconButton,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import * as XLSX from 'xlsx-js-style';
import ExcelConflictResolutionModal from './ExcelConflictResolutionModal';
import {
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  writeBatch,
  doc,
} from 'firebase/firestore';
import { auth } from './firebaseConfig';

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  backgroundColor: '#ffffff',
  color: '#000000',
  boxShadow: theme.shadows[5],
  padding: theme.spacing(4),
  maxHeight: '90vh',
  overflowY: 'auto',
  border: '1px solid #ccc',
  borderRadius: theme.spacing(1),
}));

const UploadArea = styled(Box)(({ theme }) => ({
  border: '2px dashed #ccc',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  '&.dragover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  },
}));

const ExcelImportModal = ({ isOpen, onClose, setAlertOpen, setError, setSuccessMessage }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [isConflictModalOpen, setIsConflictModalOpen] = useState(false);

  const resetModal = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setImportResults(null);
    setIsProcessing(false);
    setDragOver(false);
    setConflicts([]);
    setIsConflictModalOpen(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(fileExtension)) {
      setError('Lütfen geçerli bir Excel dosyası (.xlsx, .xls) veya CSV dosyası seçin.');
      setAlertOpen(true);
      return;
    }

    setSelectedFile(file);
    readExcelFile(file);
  };

  const readExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          setError('Excel dosyası en az 2 satır içermelidir (başlık + veri).');
          setAlertOpen(true);
          return;
        }

        // İlk satırı başlık olarak kabul et, geri kalanını veri olarak işle
        const dataRows = jsonData.slice(1).filter(row => row.length >= 2 && row[0] && row[1]);
        
        if (dataRows.length === 0) {
          setError('Excel dosyasında geçerli veri bulunamadı. İlk sütun: Cari Kod, İkinci sütun: Müşteri Adı olmalıdır.');
          setAlertOpen(true);
          return;
        }

        const processedData = dataRows.map((row, index) => ({
          id: index + 1,
          cariCode: String(row[0]).trim(),
          musteriAdi: String(row[1]).trim(),
          telefon: row[2] ? String(row[2]).trim() : '',
          email: row[3] ? String(row[3]).trim() : '',
        }));

        setPreviewData(processedData);
      } catch (error) {
        console.error('Excel dosyası okunurken hata:', error);
        setError('Excel dosyası okunurken bir hata oluştu. Dosya formatını kontrol edin.');
        setAlertOpen(true);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const checkExistingCustomers = async (customers) => {
    const userEmail = auth.currentUser?.email;
    if (!userEmail) throw new Error('Kullanıcı oturumu bulunamadı');

    const db = getFirestore();
    const customerListRef = collection(db, `users/${userEmail}/customerList`);
    
    const conflicts = [];
    const toAdd = [];

    for (const customer of customers) {
      // Cari kod kontrolü
      const cariCodeQuery = query(customerListRef, where('cariCode', '==', customer.cariCode));
      const cariCodeSnapshot = await getDocs(cariCodeQuery);

      // Müşteri adı kontrolü
      const nameQuery = query(customerListRef, where('Müşteri Adı', '==', customer.musteriAdi));
      const nameSnapshot = await getDocs(nameQuery);

      if (!cariCodeSnapshot.empty || !nameSnapshot.empty) {
        // Mevcut kaydı da al
        const existingData = !cariCodeSnapshot.empty 
          ? cariCodeSnapshot.docs[0].data()
          : nameSnapshot.docs[0].data();
        
        conflicts.push({
          ...customer,
          reason: !cariCodeSnapshot.empty ? 'Cari kod mevcut' : 'Müşteri adı mevcut',
          existingData: {
            'Müşteri Adı': existingData['Müşteri Adı'] || '',
            'cariCode': existingData['cariCode'] || '',
            'Telefon': existingData['Telefon'] || '',
            'E-posta': existingData['E-posta'] || ''
          }
        });
      } else {
        toAdd.push(customer);
      }
    }

    return { conflicts, toAdd };
  };

  const importCustomers = async () => {
    if (previewData.length === 0) {
      setError('İçe aktarılacak veri bulunamadı.');
      setAlertOpen(true);
      return;
    }

    setIsProcessing(true);

    try {
      const userEmail = auth.currentUser?.email;
      if (!userEmail) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      const { conflicts, toAdd } = await checkExistingCustomers(previewData);

      if (conflicts.length > 0) {
        // Çakışma varsa çakışma modalını aç
        setConflicts(conflicts);
        setIsConflictModalOpen(true);
        setIsProcessing(false);
        return;
      }

      if (toAdd.length === 0) {
              setImportResults({
        success: 0,
        updated: 0,
        skipped: 0,
        conflicts: 0,
        conflictList: [],
        total: previewData.length
      });
        setIsProcessing(false);
        return;
      }

      const db = getFirestore();
      const batch = writeBatch(db);
      const customerListRef = collection(db, `users/${userEmail}/customerList`);

      toAdd.forEach((customer) => {
        const newDocRef = doc(customerListRef);
        batch.set(newDocRef, {
          'Müşteri Adı': customer.musteriAdi,
          'cariCode': customer.cariCode,
          'Telefon': customer.telefon || '',
          'E-posta': customer.email || '',
          'Onay': 'Onaylandı', // Otomatik onaylanmış olarak ekle
          'Şantiye': '',
          'parentId': null,
          'userEmail': userEmail,
          'createdBy': userEmail,
          'createdAt': new Date().toISOString(),
          'importedAt': new Date().toISOString(),
          'importSource': 'excel'
        });
      });

      await batch.commit();

      setImportResults({
        success: toAdd.length,
        updated: 0,
        skipped: 0,
        conflicts: conflicts.length,
        conflictList: conflicts,
        total: previewData.length
      });

      setSuccessMessage(`${toAdd.length} müşteri başarıyla içe aktarıldı!`);
      setAlertOpen(true);

    } catch (error) {
      console.error('İçe aktarma hatası:', error);
      setError(`İçe aktarma sırasında hata oluştu: ${error.message}`);
      setAlertOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConflictResolution = async (resolutions) => {
    setIsConflictModalOpen(false);
    setIsProcessing(true);

    try {
      const userEmail = auth.currentUser?.email;
      if (!userEmail) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      const db = getFirestore();
      const batch = writeBatch(db);
      const customerListRef = collection(db, `users/${userEmail}/customerList`);

      let successCount = 0;
      let skippedCount = 0;
      let updatedCount = 0;

      for (const resolution of resolutions) {
        if (resolution.resolution === 'skip') {
          skippedCount++;
          continue;
        }

        if (resolution.resolution === 'replace') {
          // Mevcut kaydı bul ve güncelle
          const existingQuery = query(
            customerListRef, 
            where('cariCode', '==', resolution.cariCode)
          );
          const existingSnapshot = await getDocs(existingQuery);
          
          if (!existingSnapshot.empty) {
            const existingDoc = existingSnapshot.docs[0];
            batch.update(existingDoc.ref, {
              'Müşteri Adı': resolution.musteriAdi,
              'Telefon': resolution.telefon || '',
              'E-posta': resolution.email || '',
              'updatedAt': new Date().toISOString(),
              'lastImportSource': 'excel'
            });
            updatedCount++;
          }
        } else if (resolution.resolution === 'merge') {
          // Mevcut kaydı bul ve boş alanları doldur
          const existingQuery = query(
            customerListRef, 
            where('cariCode', '==', resolution.cariCode)
          );
          const existingSnapshot = await getDocs(existingQuery);
          
          if (!existingSnapshot.empty) {
            const existingDoc = existingSnapshot.docs[0];
            const existingData = existingDoc.data();
            
            const updateData = {};
            if (!existingData['Telefon'] && resolution.telefon) {
              updateData['Telefon'] = resolution.telefon;
            }
            if (!existingData['E-posta'] && resolution.email) {
              updateData['E-posta'] = resolution.email;
            }
            
            if (Object.keys(updateData).length > 0) {
              updateData['updatedAt'] = new Date().toISOString();
              updateData['lastImportSource'] = 'excel';
              batch.update(existingDoc.ref, updateData);
              updatedCount++;
            } else {
              skippedCount++;
            }
          }
        }
      }

      // Çakışması olmayan kayıtları da ekle
      const nonConflictData = previewData.filter(item => 
        !conflicts.some(conflict => conflict.cariCode === item.cariCode)
      );

      nonConflictData.forEach((customer) => {
        const newDocRef = doc(customerListRef);
        batch.set(newDocRef, {
          'Müşteri Adı': customer.musteriAdi,
          'cariCode': customer.cariCode,
          'Telefon': customer.telefon || '',
          'E-posta': customer.email || '',
          'Onay': 'Onaylandı',
          'Şantiye': '',
          'parentId': null,
          'userEmail': userEmail,
          'createdBy': userEmail,
          'createdAt': new Date().toISOString(),
          'importedAt': new Date().toISOString(),
          'importSource': 'excel'
        });
        successCount++;
      });

      await batch.commit();

      setImportResults({
        success: successCount,
        updated: updatedCount,
        skipped: skippedCount,
        conflicts: resolutions.length,
        conflictList: resolutions.filter(r => r.resolution === 'skip'),
        total: previewData.length
      });

      setSuccessMessage(
        `İşlem tamamlandı! ${successCount} yeni kayıt, ${updatedCount} güncelleme, ${skippedCount} atlandı.`
      );
      setAlertOpen(true);

    } catch (error) {
      console.error('Çakışma çözümü hatası:', error);
      setError(`Çakışma çözümü sırasında hata oluştu: ${error.message}`);
      setAlertOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal 
      open={isOpen} 
      onClose={() => {}} 
      disableEscapeKeyDown={true}
    >
      <ModalBox>
        <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
          <Typography variant="h5" component="h2" textAlign="center">
            📊 Excel'den Müşteri İçe Aktarma
          </Typography>
        </Box>

        {!selectedFile && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>📋 Excel Formatı:</strong><br />
              • <strong>1. Sütun:</strong> Cari Kod (A-001, B-002 vb.) - <span style={{color: 'red'}}>Zorunlu</span><br />
              • <strong>2. Sütun:</strong> Müşteri Adı - <span style={{color: 'red'}}>Zorunlu</span><br />
              • <strong>3. Sütun:</strong> Telefon - <span style={{color: 'green'}}>Opsiyonel</span><br />
              • <strong>4. Sütun:</strong> E-posta - <span style={{color: 'green'}}>Opsiyonel</span><br />
              <br />
              ✅ Tüm müşteriler otomatik olarak <strong>onaylanmış</strong> durumda eklenecektir.
            </Alert>

            <UploadArea
              className={dragOver ? 'dragover' : ''}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('excel-file-input').click()}
            >
              <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Excel Dosyasını Sürükleyip Bırakın
              </Typography>
              <Typography variant="body2" color="textSecondary">
                veya tıklayarak dosya seçin
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Desteklenen formatlar: .xlsx, .xls, .csv
              </Typography>
            </UploadArea>

            <input
              id="excel-file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={(e) => handleFileSelect(e.target.files[0])}
            />
          </>
        )}

        {selectedFile && previewData.length > 0 && !importResults && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <strong>{selectedFile.name}</strong> dosyası başarıyla okundu. 
              {previewData.length} müşteri kaydı bulundu.
            </Alert>

            <Typography variant="h6" gutterBottom>
              📋 Önizleme (İlk 10 kayıt):
            </Typography>

            <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #eee', mb: 2 }}>
              {previewData.slice(0, 10).map((customer, index) => (
                <React.Fragment key={customer.id}>
                  <ListItem>
                    <ListItemText
                      primary={`${customer.cariCode} - ${customer.musteriAdi}`}
                      secondary={
                        customer.telefon || customer.email
                          ? `${customer.telefon ? 'Tel: ' + customer.telefon : ''} ${customer.email ? 'Email: ' + customer.email : ''}`
                          : 'İletişim bilgisi yok'
                      }
                    />
                  </ListItem>
                  {index < 9 && index < previewData.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {previewData.length > 10 && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                ... ve {previewData.length - 10} kayıt daha
              </Typography>
            )}

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleClose}>
                İptal
              </Button>
              <Button
                variant="contained"
                onClick={importCustomers}
                disabled={isProcessing}
                startIcon={<CheckCircleIcon />}
              >
                {isProcessing ? 'İçe Aktarılıyor...' : `${previewData.length} Müşteriyi İçe Aktar`}
              </Button>
            </Box>

            {isProcessing && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Müşteriler veritabanına ekleniyor...
                </Typography>
              </Box>
            )}
          </>
        )}

        {importResults && (
          <>
            <Alert 
              severity={importResults.success > 0 ? "success" : "warning"} 
              sx={{ mb: 2 }}
            >
              <strong>İçe Aktarma Tamamlandı!</strong>
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                📊 İşlem Sonucu:
              </Typography>
              
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircleIcon color="success" />
                <Typography>
                  <strong>{importResults.success}</strong> yeni müşteri eklendi
                </Typography>
              </Box>

              {importResults.updated > 0 && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CheckCircleIcon color="primary" />
                  <Typography>
                    <strong>{importResults.updated}</strong> müşteri güncellendi
                  </Typography>
                </Box>
              )}

              {importResults.skipped > 0 && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <ErrorIcon color="warning" />
                  <Typography>
                    <strong>{importResults.skipped}</strong> müşteri atlandı
                  </Typography>
                </Box>
              )}

              <Typography variant="body2" color="textSecondary">
                Toplam işlenen kayıt: {importResults.total}
              </Typography>
            </Box>

            {importResults.conflictList && importResults.conflictList.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  ⚠️ Çakışan Kayıtlar:
                </Typography>
                <List sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #eee', mb: 2 }}>
                  {importResults.conflictList.map((conflict, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={`${conflict.cariCode} - ${conflict.musteriAdi}`}
                          secondary={conflict.reason}
                        />
                      </ListItem>
                      {index < importResults.conflictList.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </>
            )}

            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetModal}>
                Yeni İçe Aktarma
              </Button>
              <Button variant="contained" onClick={handleClose}>
                Kapat
              </Button>
            </Box>
          </>
        )}

        <ExcelConflictResolutionModal
          isOpen={isConflictModalOpen}
          onClose={() => setIsConflictModalOpen(false)}
          conflicts={conflicts}
          onResolve={handleConflictResolution}
        />
      </ModalBox>
    </Modal>
  );
};

export default ExcelImportModal; 